import { letterheadClasses, plain, StationeryHead } from '@app/stationery'
import type { LuachShul } from './types'

/**
 * A year of Shabbosim as a table: one row per week, times across the columns.
 *
 * The artifact that replaces two of the workbook's printed sheets at once — the
 * year-wide grid and the separate candle-lighting sheet, which differ only in
 * which columns they carry. Which columns is the caller's; this decides only how
 * they sit on paper.
 *
 * It PAGINATES rather than shrinking, for the roster's reason: a year is not a
 * fixed thing that belongs on one sheet, and a table that got smaller every time
 * a shul added a minyan would eventually be unreadable.
 *
 * The masthead is COMPACT and repeats on every page. Compact because every
 * story the head takes is a row the sheet does not fit; on every page because
 * each page is a sheet somebody might hang or hand out on its own, and one that
 * does not say whose shul it is says nothing.
 */

export type YearTableColumn = {
  id: string
  labelHe: string
  labelEn: string
  /** Which day it reads. Null for a column about the week itself. */
  role?: 'erev' | 'day' | null
  /**
   * `fact` states something about the week; `zman` and `line` both carry a
   * time — one the sun's, one the shul's — and print alike, so everything here
   * except identity treats the two together.
   */
  family: 'fact' | 'zman' | 'line'
}

export type YearTableCell = {
  time: string | null
  displayText: string | null
  parts?: Array<{ labelHe: string; time: string | null }>
  overrideNote: string | null
}

export type YearTableRow = {
  date: string
  labelHe: string
  /** Keyed by column id — a time the shul computed. */
  cells: Record<string, YearTableCell>
  /** Keyed by column id — a fact about the week, already read. */
  facts: Record<string, string>
}

/**
 * How many leading columns identify the ROW rather than time it.
 *
 * The leading run of fact columns: a sheet leads with what the week IS and then
 * lists its times, so that run is the row's name. It is what repeats when the
 * columns are too wide for one page and have to continue sideways — a
 * continuation band of bare times with nothing saying which week each belongs to
 * would be unreadable, which is the whole reason the concept exists.
 *
 * A fact column placed AFTER a time is not identity. That is not a special case
 * to regret: somebody who puts the molad between two minyanim has said it reads
 * as one of the columns, not as part of the row's name.
 */
export function identityCount(columns: YearTableColumn[]): number {
  let n = 0
  while (n < columns.length && columns[n].family === 'fact') n++
  // Never every column: a band of nothing but identity carries no information,
  // and a set that is all facts is one band anyway.
  return Math.min(n, Math.max(columns.length - 1, 0))
}

/**
 * What a column is called ON THIS SHEET.
 *
 * A bare name means SHABBOS. Every row of this chart is a Shabbos and its erev,
 * so the Shabbos is the unmarked case and anything that is not on it says so:
 * the erev mincha is `מנחה - ע״ש`, and maariv is `מעריב - מוצ״ש` because it is
 * after Shabbos rather than during it.
 *
 * Derived from the role and the tefilla rather than written into the label,
 * because the label is the shul's word for the thing and the qualifier is this
 * sheet's word for when. On the chag's own sheet neither is needed — the title
 * says which day it is.
 */
const QUALIFIER = {
  erev: { he: 'ע״ש', en: 'Erev' },
  motzei: { he: 'מוצ״ש', en: 'Motzei' },
} as const

export function headings(
  columns: YearTableColumn[],
): { he: string; en: string }[] {
  return columns.map((c) => {
    const bare = { he: c.labelHe, en: c.labelEn }
    const qualify = (q: { he: string; en: string }) => ({
      he: `${c.labelHe} - ${q.he}`,
      en: `${q.en} ${c.labelEn.charAt(0).toLowerCase()}${c.labelEn.slice(1)}`,
    })

    /*
     * A LINE takes the qualifier only when it is a tefilla. It is there to tell
     * apart a thing that happens on both halves of the row — a mincha davens
     * erev and Shabbos both — and nothing else the shul places does: הדלקת נרות
     * is only ever the erev and צאת הכוכבים only ever the day, so saying so
     * states what the name already says.
     */
    if (c.id.startsWith('line:')) {
      if (!c.id.startsWith('line:tefilla:')) return bare
      if (c.role === 'erev') return qualify(QUALIFIER.erev)
      // Maariv on the Shabbos itself is motzei Shabbos: the row's day has ended.
      if (c.id.includes(':maariv:')) return qualify(QUALIFIER.motzei)
      return bare
    }

    /*
     * A zman or a date takes it on the erev, always. Both days have an alos and
     * a date, so the name alone names neither — which is exactly the case the
     * qualifier exists for, and it is why the label itself never carries one.
     */
    return c.role === 'erev' ? qualify(QUALIFIER.erev) : bare
  })
}

/** A time with its meridiem set quiet, the way the sheet does everywhere else. */
function Time({ value }: { value: string }) {
  const m = /^(\d{1,2}:\d{2})\s*(AM|PM)$/i.exec(value.trim())
  if (!m) return <>{value}</>
  return (
    <>
      {m[1]}
      <span className="ycell__meridiem">{m[2].toLowerCase()}</span>
    </>
  )
}

/** What one cell prints. Empty renders blank — never a placeholder. */
function Cell({ column, row }: { column: YearTableColumn; row: YearTableRow }) {
  if (column.family === 'fact') {
    /*
     * A fact may carry a quieter second part — the month a Shabbos Mevorchim
     * bentches, which belongs TO the day rather than beside it. Set on the same
     * line, because the row's height is stated and a second line would break it.
     */
    const [main, sub] = (row.facts[column.id] ?? '').split('\n')
    return (
      <>
        {plain(main ?? '')}
        {sub ? <span className="ycell__sub">{plain(sub)}</span> : null}
      </>
    )
  }
  const cell = row.cells[column.id]
  if (!cell) return null

  // A composite prints its opinions side by side, the way the sheet's own line
  // does — the column is one column and the opinions are what it holds.
  if (cell.parts?.length) {
    return (
      <>
        {cell.parts.map((p, i) => (
          <span key={p.labelHe} className="ycell__part">
            {i > 0 ? <span className="ycell__sep"> / </span> : null}
            {p.time ? <Time value={p.time} /> : null}
          </span>
        ))}
      </>
    )
  }

  const text = cell.displayText ?? cell.time
  if (!text) return null
  return (
    <>
      {/* Printed INSTEAD of the time — `אחר מנחה` — so it is Hebrew prose, not
          a figure. The cell is set for times, and left to inherit that a
          sentence comes out monospaced, left to right and spaced like digits. */}
      {cell.displayText ? (
        <span className="ycell__said">{plain(cell.displayText)}</span>
      ) : (
        <Time value={text} />
      )}
      {/* The workbook asterisked an overridden time and footnoted the reason;
          the mark is the sheet's own convention and travels with the value. */}
      {cell.overrideNote ? <span className="ycell__mark">*</span> : null}
    </>
  )
}

export function YearTable({
  shul,
  titleHe,
  columns,
  rows,
  orientation = 'portrait',
  showSubtitle = false,
  print = false,
}: {
  shul: LuachShul
  /** What this sheet is — the year, in the shul's own words. */
  titleHe: string
  columns: YearTableColumn[]
  rows: YearTableRow[]
  orientation?: 'portrait' | 'landscape'
  /** The quiet English gloss under each Hebrew column head. */
  showSubtitle?: boolean
  print?: boolean
}) {
  const head = letterheadClasses(shul.letterhead, 'compact').join(' ')
  const ids = identityCount(columns)
  const heads = headings(columns)

  return (
    <article
      className={[
        'ytable',
        `ytable--${orientation}`,
        print ? 'ytable--print' : '',
        showSubtitle ? '' : 'ytable--no-subtitle',
      ]
        .filter(Boolean)
        .join(' ')}
      /* The paginator reads these rather than being told them twice: the
         markup already knows which columns name the row. */
      data-identity={ids}
    >
      {/*
       * The masthead, held once here for the paginator to clone onto each page.
       * Cloned rather than rendered per page because how many pages there are is
       * not known until the rows have been measured.
       */}
      <div className={`ytable__masthead ${head}`}>
        <StationeryHead shul={shul} />
        <div className="ytable__titles">
          <h1 className="ytable__title">{plain(titleHe)}</h1>
        </div>
      </div>

      {/*
       * Every row in one table, for measuring. `paginateYearTable` empties this
       * into the pages below; what is left without script is the whole year in
       * one long table, which is the honest fallback.
       */}
      <div className="ytable__flow">
        <table className="ytable__grid">
          <colgroup>
            {columns.map((c, i) => (
              <col
                key={c.id}
                className={`ycol ycol--${i < ids ? 'id' : c.family}`}
              />
            ))}
          </colgroup>
          <thead>
            <tr className="yhead">
              {columns.map((c, i) => (
                <th key={c.id} className="ycell ycell--head" scope="col">
                  <span className="yhead__he">{plain(heads[i].he)}</span>
                  <span className="yhead__en">{heads[i].en}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr className="yrow" key={row.date}>
                {columns.map((c, i) => (
                  <td
                    key={c.id}
                    className={`ycell ycell--${i < ids ? 'id' : c.family}`}
                  >
                    <Cell column={c} row={row} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ytable__pages" />
    </article>
  )
}
